import { Autocomplete, AutocompleteProps, AutocompleteRenderInputParams, AutocompleteValue, CircularProgress, TextField } from '@mui/material';
import { ReactNode, useState } from 'react';
import { RefCallBack } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useServerSearch, UseServerSearchOptions } from './useServerSearch';

export interface ServerAutocompleteProps<T, Multiple extends boolean = false> extends 
    Omit<AutocompleteProps<T, Multiple, false, false>, 'options' | 'renderInput' | 'loading' | 'onChange'> 
{
    onChange: (value: AutocompleteValue<T, Multiple, false, false>) => void;

    getOptionLabel?: (option: T) => string;
    getOptionId?: (option: T) => string;
    renderHighlightedLabel?: (option: T, query: string) => ReactNode;
    search: UseServerSearchOptions<T> & { preloadOnOpen?: boolean };
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    error?: boolean;
    helperText?: ReactNode;
    inputRef?: RefCallBack;
    required?: boolean;

    renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
}

export default function ServerSearchAutocomplete<T, Multiple extends boolean = false>(props: ServerAutocompleteProps<T, Multiple>) {
    const {
        value,
        onChange,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getOptionLabel = (o: any) => String(o?.name ?? ''),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getOptionId = (o: any) => String(o?.id ?? ''),
        renderHighlightedLabel,
        inputRef,
        search,
        label,
        placeholder,
        size,
        disabled,
        error,
        helperText,
        renderInput,
        multiple,
        required
    } = props;

    const { t } = useTranslation('common');
    const [ inputValue, setInputValue ] = useState('');
    const { options, loading, error: fetchError, prefetch } = useServerSearch<T>(inputValue, search);

    const handleOpen = () => {
        if (search.preloadOnOpen) {
            prefetch();
        }
    };

    return (
        <Autocomplete<T, Multiple, false, false>
            multiple={ multiple }
            options={ options }
            value={ value }
            onChange={ (_, v) => onChange(v) }
            onOpen={ handleOpen }
            inputValue={ inputValue }
            onInputChange={ (_, v, reason) => {
                // In multi-select, MUI fires a spurious blank onInputChange (reason: 'reset')
                // right after a selection, which would wipe the typed query. Ignore it.
                if (multiple && reason === 'reset') {
                    return;
                }

                setInputValue(v);
            } }
            disabled={ disabled }
            getOptionLabel={ getOptionLabel }
            isOptionEqualToValue={ (o, v) => getOptionId(o) === getOptionId(v) }
            loading={ loading }
            size={ size }
            noOptionsText={
                inputValue.trim().length < (search.minLength ?? 2)
                    ? t('inputs.autocomplete.typeAtLeast', { count: search.minLength ?? 2 })
                    : t('inputs.autocomplete.noOptionsText')
            }
            renderOption={ (props, option) => (
                <li { ...props }
                    key={ getOptionId(option) }>
                    { renderHighlightedLabel
                        ? renderHighlightedLabel(option, inputValue)
                        : getOptionLabel(option) }
                </li>
            ) }
            renderInput={ (params) =>
                (renderInput ? (
                    renderInput(params)
                ) : (
                    <TextField
                        { ...params }
                        label={ label }
                        placeholder={ placeholder }
                        size={ size }
                        error={ error || !!fetchError }
                        helperText={ fetchError ? t('inputs.autocomplete.fetchError') : helperText }
                        inputRef={ inputRef }
                        required={ required }
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        { loading ? <CircularProgress size={ 18 } /> : null }
                                        { params.InputProps.endAdornment }
                                    </>
                                )
                            } }}
                    />
                ))
            }
        />
    );
}
